/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 28th 2024, 5:02:34 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { AbstractImageUpload, WritableModel, AbstractFileUpload } from "lib/schemas/abstract";
import { Organization } from "lib/schemas/organizations/Organization";
import { fileBaseSchema, schemaBuilder } from "lib/validation";

export interface MediaLibrary extends WritableModel<true> {
  organization: Organization["id"];
  name: string;
  description?: string;
}

export interface MediaLibraryImage extends WritableModel<true>, AbstractImageUpload {
  library: MediaLibrary["id"];
}

export interface MediaLibraryFile extends WritableModel<true>, AbstractFileUpload {
  library: MediaLibrary["id"];
}

export const MediaLibraryImageValidationSchema: schemaBuilder.SchemaOf<
  Pick<MediaLibraryImage, "name" | "caption" | "image" | "library">
> = schemaBuilder.object().shape({
  image: fileBaseSchema({ sizeLimit: null }).required("Image is required"),
  library: schemaBuilder.string().uuid().nullable(),
  name: schemaBuilder.string().max(200).optional().nullable(),
  caption: schemaBuilder.string().max(200).optional().nullable(),
});

export const MediaLibraryFileValidationSchema: schemaBuilder.SchemaOf<
  Pick<MediaLibraryFile, "name" | "file" | "library">
> = schemaBuilder.object().shape({
  file: fileBaseSchema({ sizeLimit: null }).required("File is required"),
  library: schemaBuilder.string().uuid().nullable(),
  name: schemaBuilder.string().max(200).optional().nullable(),
});

export type MediaAssetType = "image" | "file";

export type TMediaAssetMetaData = {
  media_library_id: MediaLibrary["id"];
  media_type: MediaAssetType;
  media_item_id: string;
  media_item_url?: string;
  media_item_name?: string;
};

export const MediaAssetMetaDataValidationSchema: schemaBuilder.SchemaOf<TMediaAssetMetaData | string> = schemaBuilder
  .mixed()
  .test(
    "object-or-string-or-null",
    "Must be either an object with specific properties or a string or null",
    (value) => {
      if (typeof value === "string" || value === null) return true; // Validates if the value is a string or null
      if (typeof value === "object") {
        // Further validation if the value is an object
        const objectSchema = schemaBuilder.object({
          media_library_id: schemaBuilder.string().uuid().required("Media Library is required"),
          media_type: schemaBuilder.mixed().oneOf(["image", "file"]).required("Media Type is required"),
          media_item_id: schemaBuilder.string().uuid().required("Media Item ID is required"),
          media_item_url: schemaBuilder.string().optional(),
          media_item_name: schemaBuilder.string().optional(),
        });
        return objectSchema.isValidSync(value);
      }
      return false; // Fails validation for types other than object or string or null
    }
  );
